/**
 * @file 用于存放神策插件
 */
import { invoke, buildAppPathReg, WalletChainTypes } from '@web3/base-utils';
import { getSensors } from './index';
import { WEB3_EVENT, PageViewConfigInfo } from './types';

export interface WalletInfo {
  chainType: number;
  address: string;
  walletType: number;
  sourcename: string;
  walletId: string;
}

// 注册page name映射插件
export function installPageNameMapperPlugin(
  appBase: string,
  pageViewConfig: PageViewConfigInfo[]
) {
  if (!appBase) {
    return;
  }
  // console.log(`Installing`, pageViewConfig);
  // 注册全局的page name 映射插件
  const appReg = buildAppPathReg(appBase, 'withLang') as RegExp;

  const sensors = getSensors();
  // @ts-ignore
  sensors?.registerPropertyPlugin({
    properties: function (data: any) {
      // 已有page_name的情况下，不覆盖
      if (data.properties.page_name) {
        return;
      }
      const url = new URL(data.properties.$url);
      const inAppPath = url.pathname.replace(appReg, '');
      // console.log(url, inAppPath);
      const pageConfig = pageViewConfig.find(({ page_url }) => {
        if (typeof page_url === 'string') {
          return page_url === inAppPath;
        } else if (typeof page_url === 'function') {
          return page_url(url);
        } else if (page_url instanceof RegExp) {
          return page_url.test(inAppPath);
        }
        return false;
      });
      if (!pageConfig) {
        return;
      }
      const pageName = invoke(() => {
        const { page_name } = pageConfig;
        if (typeof page_name === 'string') {
          return page_name;
        }
        if (typeof page_name === 'function') {
          return page_name(url);
        }
        const pageNames = Object.entries(page_name);
        for (const [pageName, tester] of pageNames) {
          if (typeof tester === 'string') {
            if (url.search.indexOf(pageName) !== -1) {
              return pageName;
            }
            return;
          } else if (typeof tester === 'function') {
            if (tester(url)) {
              return pageName;
            }
          } else if (tester instanceof RegExp) {
            if (tester.test(url.search)) {
              return pageName;
            }
          } else if (tester) {
            const { key, value } = tester;
            if (url.searchParams.get(key) === value) {
              return pageName;
            }
          }
        }
      });
      if (pageName) {
        data.properties.page_name = pageName;
      }
      // console.log(data.properties);
    },
  });
}

export function change$pageViewToWeb3PageView() {
  const sensors = getSensors();
  // @ts-ignore
  sensors?.registerPropertyPlugin({
    properties(data: any) {
      if (data.event === '$pageview') {
        data.event = WEB3_EVENT.Web3PageView;
      }
    },
  });
}

let walletInfo: WalletInfo = {
  address: '',
  sourcename: '',
  walletType: 0,
  chainType: 0,
  walletId: '',
};

/**
 * 自动添加钱包统一埋点参数
 */
export const addWalletCommonParamPlugin = () => {
  const sensors = getSensors();
  // @ts-ignore
  sensors?.registerPropertyPlugin({
    properties(data: any) {
      // 不是web3埋点则直接返回
      if (
        data.event !== WEB3_EVENT.Web3PageView &&
        data.event !== WEB3_EVENT.Web3UserClick
      )
        return;

      data.properties.wallet_chain_type =
        data.properties.wallet_chain_type ??
        (WalletChainTypes[walletInfo.chainType as WalletChainTypes] || '');
      data.properties.wallet_address =
        data.properties.wallet_address ?? (walletInfo.address || '');
      data.properties.wallet_type =
        data.properties.wallet_type ?? `${walletInfo.walletType || ''}`;
      data.properties.wallet_sourcename =
        data.properties.wallet_sourcename ?? (walletInfo.sourcename || '');
      data.properties.wallet_id =
        data.properties.wallet_id ?? (walletInfo.walletId || '');
    },
  });
};

// 设置钱包统一埋点参数
export const setTrackWalletInfo = (v: Partial<WalletInfo>) => {
  walletInfo = {
    ...walletInfo,
    ...v,
  };
};
